import type { FC } from 'react'

import { Select, Textarea } from '@skytech/ui'
import { toFirstUpperCase } from '@skytech/utils'
import { uniq } from '@skytech/utils/list-operations'
import { useQuery } from '@tanstack/react-query'
import type { FieldProps } from 'formik'
import { Field } from 'formik'
import GlobeIcon from 'mdi-react/GlobeIcon'
import PinIcon from 'mdi-react/PinIcon'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import type { Policy } from '@/resources/ecom-policy'

import { getSelectOptions } from '../helpers'

import queries from '../../queries'
import adminResources from '../../resources'

export const Status: FC<{ ecomPolicies: Policy[] }> = ({ ecomPolicies }) => {
  const translate = useTranslation()

  const { data: assetTypes } = useQuery(queries.adminAssetTypes(), adminResources.getAssetTypes)
  const { data: addresses } = useQuery(queries.adminAddresses(), adminResources.getAddressList)
  const addressOptions =
    addresses?.map(({ id, zip, city, address }) => ({
      value: id,
      label: `${address}, ${zip} ${city}`,
    })) || []

  return (
    <div>
      <div className="wrap flex">
        <div className="mx-2 flex flex-1 basis-[30rem] flex-col rounded-lg">
          <Field name="statusId">
            {({ field, form }: FieldProps) => (
              <Select
                capitalizeLabel
                id="statusId"
                label={
                  <>
                    <Translation id="Status" />*
                  </>
                }
                options={
                  assetTypes?.statuses.map(({ id, status }) => ({
                    value: id,
                    label: toFirstUpperCase(translate(`asset_status/${status}`)),
                  })) || []
                }
                marginBottom={16}
                {...field}
                onChange={option => form.setFieldValue(field.name, option?.value)}
              />
            )}
          </Field>

          <Field name="customerAddressId">
            {({ field, form }: FieldProps) => (
              <Select
                capitalizeLabel
                id="customerAddressId"
                label={<Translation id="Location" />}
                options={uniq(addressOptions)}
                marginBottom={16}
                {...field}
                onChange={option => form.setFieldValue(field.name, option?.value)}
              />
            )}
          </Field>
        </div>
        <div className="mx-2 flex flex-1 basis-[30rem] flex-col rounded-lg">
          <Field name="ownershipTypeId">
            {({ field, form }: FieldProps) => (
              <Select
                capitalizeLabel
                id="ownershipTypeId"
                label={
                  <>
                    <Translation id="Ownership" />*
                  </>
                }
                options={
                  getSelectOptions(
                    assetTypes?.ownerships.map(({ id, textId, name }) => ({
                      id,
                      label: name ? { id: textId, values: { name } } : textId,
                    })),
                    translate
                  ) || []
                }
                marginBottom={16}
                {...field}
                onChange={option => form.setFieldValue(field.name, option?.value)}
              />
            )}
          </Field>

          {/* Mandatory when user har available policies */}
          <Field name="ecomPolicyId">
            {({ field, form }: FieldProps) => (
              <Select
                capitalizeLabel
                id="ecomPolicyId"
                label={
                  <>
                    <Translation id="Ecom policy" />
                    {ecomPolicies && ecomPolicies.length > 0 && '*'}
                  </>
                }
                options={(ecomPolicies || []).map(policy => ({
                  label: (
                    <span>
                      {policy.customerHeadName ? (
                        <GlobeIcon className="mb-0.5 mr-1 h-4 w-4 fill-gray-500" />
                      ) : (
                        <PinIcon className="mb-0.5 mr-1 h-4 w-4 fill-gray-500" />
                      )}
                      {policy.name}
                    </span>
                  ),
                  value: policy.id,
                }))}
                marginBottom={16}
                {...field}
                onChange={option => form.setFieldValue(field.name, option?.value)}
              />
            )}
          </Field>
        </div>
      </div>

      <div className="mx-2">
        <Field name="comment">
          {({ field, meta }: FieldProps) => (
            <Textarea
              capitalizeLabel
              isFullWidth
              id="comment"
              marginBottom={16}
              touched={meta.touched}
              error={meta.error}
              label={<Translation id="Note" />}
              {...field}
            />
          )}
        </Field>
      </div>
    </div>
  )
}
